import { useState, useEffect } from 'react';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import { 
  Plus, 
  Pencil, 
  Trash2, 
  Search,
  Users,
  Shield,
  User,
  Check,
  X
} from 'lucide-react';

export default function UsersAdminPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'user',
    can_create_ilots: true,
    can_create_machines: true,
    can_create_planning: true
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/users`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      } else if (response.status === 403) {
        toast.error('Accès refusé. Réservé aux administrateurs.');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Erreur lors du chargement des utilisateurs');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreate = () => {
    setEditingUser(null);
    setFormData({
      email: '',
      password: '',
      name: '',
      role: 'user',
      can_create_ilots: true,
      can_create_machines: true,
      can_create_planning: true
    });
    setDialogOpen(true);
  };

  const handleOpenEdit = (user) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      password: '',  // Don't populate password on edit
      name: user.name,
      role: user.role || 'user',
      can_create_ilots: user.can_create_ilots ?? true,
      can_create_machines: user.can_create_machines ?? true,
      can_create_planning: user.can_create_planning ?? true
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Le nom est obligatoire');
      return;
    }

    if (!editingUser && !formData.password) {
      toast.error('Le mot de passe est obligatoire');
      return;
    }

    try {
      const url = editingUser 
        ? `${API}/users/${editingUser.user_id}`
        : `${API}/users`;
      
      const payload = editingUser 
        ? {
            name: formData.name,
            role: formData.role,
            can_create_ilots: formData.can_create_ilots,
            can_create_machines: formData.can_create_machines,
            can_create_planning: formData.can_create_planning,
            ...(formData.password && { password: formData.password })  // Only include if provided
          }
        : formData;

      const response = await fetch(url, {
        method: editingUser ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success(editingUser ? 'Utilisateur modifié' : 'Utilisateur créé');
        setDialogOpen(false);
        fetchUsers();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur');
      }
    } catch (error) {
      console.error('Error saving user:', error);
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const handleDelete = async (userId, userName) => {
    if (!window.confirm(`Supprimer l'utilisateur "${userName}" ?`)) return;

    try {
      const response = await fetch(`${API}/users/${userId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Utilisateur supprimé');
        fetchUsers();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la suppression');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const filteredUsers = users.filter(user => 
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const PermissionIcon = ({ granted }) => (
    granted ? (
      <Check className="w-4 h-4 text-green-600" />
    ) : (
      <X className="w-4 h-4 text-red-400" />
    )
  );

  return (
    <div className="space-y-6" data-testid="users-admin-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-3xl tracking-tight uppercase">
            Gestion Utilisateurs
          </h1>
          <p className="text-muted-foreground mt-1 font-mono text-sm uppercase tracking-wider">
            Comptes et permissions
          </p>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-2">
        <Button onClick={handleOpenCreate} className="btn-industrial rounded-sm" data-testid="create-user-btn">
          <Plus className="w-4 h-4 mr-2" />
          Nouvel Utilisateur
        </Button>
      </div>

      {/* Search */}
      <Card className="border-2 rounded-sm">
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
            <Input
              placeholder="Rechercher un utilisateur..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 rounded-sm border-2"
            />
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card className="border-2 rounded-sm">
        <CardHeader className="border-b border-border">
          <CardTitle className="font-heading font-semibold text-sm uppercase tracking-tight flex items-center gap-2">
            <Users className="w-4 h-4 text-primary" />
            Utilisateurs ({filteredUsers.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <p className="text-muted-foreground">Chargement...</p>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Users className="w-16 h-16 text-muted-foreground mb-4" strokeWidth={1} />
              <h3 className="font-heading font-semibold text-xl uppercase tracking-tight mb-2">
                {searchTerm ? 'Aucun résultat' : 'Aucun utilisateur'}
              </h3>
              <p className="text-muted-foreground text-center mb-4">
                {searchTerm ? 'Aucun utilisateur ne correspond à votre recherche' : 'Créez votre premier utilisateur'}
              </p>
              {!searchTerm && (
                <Button onClick={handleOpenCreate} className="btn-industrial rounded-sm">
                  <Plus className="w-4 h-4 mr-2" />
                  Nouvel Utilisateur
                </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="font-mono uppercase text-xs">Nom</TableHead>
                  <TableHead className="font-mono uppercase text-xs">Email</TableHead>
                  <TableHead className="font-mono uppercase text-xs">Rôle</TableHead>
                  <TableHead className="font-mono uppercase text-xs text-center">Îlots</TableHead>
                  <TableHead className="font-mono uppercase text-xs text-center">Machines</TableHead>
                  <TableHead className="font-mono uppercase text-xs text-center">Planning</TableHead>
                  <TableHead className="font-mono uppercase text-xs text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.map((user) => (
                  <TableRow key={user.user_id} data-testid={`user-row-${user.user_id}`}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                          <User className="w-4 h-4 text-primary" />
                        </div>
                        {user.name}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{user.email}</TableCell>
                    <TableCell>
                      {user.role === 'admin' ? (
                        <Badge variant="default" className="rounded-sm gap-1">
                          <Shield className="w-3 h-3" />
                          Admin
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="rounded-sm">
                          User
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <PermissionIcon granted={user.can_create_ilots ?? true} />
                    </TableCell>
                    <TableCell className="text-center">
                      <PermissionIcon granted={user.can_create_machines ?? true} />
                    </TableCell>
                    <TableCell className="text-center">
                      <PermissionIcon granted={user.can_create_planning ?? true} />
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenEdit(user)}
                          className="rounded-sm h-8"
                          data-testid={`edit-user-${user.user_id}`}
                        >
                          <Pencil className="w-3 h-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(user.user_id, user.name)}
                          className="rounded-sm h-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-user-${user.user_id}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-sm max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-tight">
              {editingUser ? 'Modifier l\'Utilisateur' : 'Nouvel Utilisateur'}
            </DialogTitle>
            <DialogDescription>
              {editingUser ? 'Modifiez les informations et permissions' : 'Créez un nouveau compte utilisateur'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="name">Nom *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ex: Jean Dupont"
                  className="rounded-sm border-2"
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="jean.dupont@taptools.fr"
                  className="rounded-sm border-2"
                  required
                  disabled={!!editingUser}  // Can't change email on edit
                />
              </div>
              
              <div>
                <Label htmlFor="password">
                  Mot de passe {editingUser ? '(laisser vide pour ne pas changer)' : '*'}
                </Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="••••••••"
                  className="rounded-sm border-2"
                  required={!editingUser}
                />
              </div>

              <div>
                <Label htmlFor="role">Rôle</Label>
                <Select
                  value={formData.role}
                  onValueChange={(value) => setFormData({ ...formData, role: value })}
                >
                  <SelectTrigger className="rounded-sm border-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4" />
                        Utilisateur
                      </div>
                    </SelectItem>
                    <SelectItem value="admin">
                      <div className="flex items-center gap-2">
                        <Shield className="w-4 h-4" />
                        Administrateur
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="border-t pt-4">
                <Label className="mb-3 block">Permissions</Label>
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="can_create_ilots"
                      checked={formData.can_create_ilots}
                      onCheckedChange={(checked) => setFormData({ ...formData, can_create_ilots: checked })}
                    />
                    <label htmlFor="can_create_ilots" className="text-sm font-medium cursor-pointer">
                      Créer des îlots
                    </label>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="can_create_machines"
                      checked={formData.can_create_machines}
                      onCheckedChange={(checked) => setFormData({ ...formData, can_create_machines: checked })}
                    />
                    <label htmlFor="can_create_machines" className="text-sm font-medium cursor-pointer">
                      Créer des machines
                    </label>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="can_create_planning"
                      checked={formData.can_create_planning}
                      onCheckedChange={(checked) => setFormData({ ...formData, can_create_planning: checked })}
                    />
                    <label htmlFor="can_create_planning" className="text-sm font-medium cursor-pointer">
                      Créer des plannings (actions)
                    </label>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-3">
                  Les administrateurs ont toutes les permissions par défaut
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm border-2">
                Annuler
              </Button>
              <Button type="submit" className="btn-industrial rounded-sm">
                {editingUser ? 'Modifier' : 'Créer'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
